"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IntlProvider = exports.createIntlProvider = void 0;
const react_1 = __importStar(require("react"));
const react_intl_1 = require("react-intl");
const util_1 = require("../lib/util");
const constants_1 = require("../constants");
const useNodeHighlighter_1 = require("../lib/useNodeHighlighter");
/**
 * Creates an IntlProvider component that provides internationalization context to its children.
 *
 * @returns {React.FC<IntlProviderProps>} The IntlProvider component.
 *
 * The IntlProvider component uses the `useMemo` hook to create an `intl` object that is used to format messages.
 * It supports debugging by prefixing formatted messages with a debug prefix if the `debug` prop is set or a specific cookie is present.
 *
 * The IntlProvider also restores the HTML element support that was removed in `react-intl` v4.
 *
 * @example
 * ```tsx
 * const MyIntlProvider = createIntlProvider();
 *
 * const App = () => (
 *   <MyIntlProvider locale="en-US" messages={messages}>
 *     <MyComponent />
 *   </MyIntlProvider>
 * );
 * ```
 *
 * @typedef {Object} IntlProviderProps
 * @property {React.ReactNode} children - The child components that will have access to the internationalization context.
 * @property {boolean} [debug] - Optional. If true, formatted messages will include debug information.
 * @property {string} [debugPrefix] - Optional. Leading character when displaying ID in debug mode.
 * @property {string} [debugSuffix] - Optional. Trailing character when displaying ID in debug mode.
 * @property {Object} [values] - Optional. Default values for message variables.
 * @property {Object} [defaultRichTextElements] - Optional. Default rich text elements for message formatting.
 * @property {string} [locale] - Optional. The locale to use for formatting messages.
 * @property {Object} [messages] - Optional. The messages to use for formatting.
 */
const createIntlProvider = () => {
    const intlCache = (0, react_intl_1.createIntlCache)();
    const InternalIntlProvider = (_a) => {
        var _b, _c;
        var { children, debug } = _a, props = __rest(_a, ["children", "debug"]);
        const debugCookie = typeof window !== 'undefined' &&
            ((_c = (_b = window === null || window === void 0 ? void 0 : window.document) === null || _b === void 0 ? void 0 : _b.cookie) === null || _c === void 0 ? void 0 : _c.indexOf(`${constants_1.DEBUG_COOKIE_NAME}=${constants_1.DEBUG_COOKIE_VALUE}`)) > -1;
        const intl = (0, react_1.useMemo)(() => {
            const _a = (0, util_1.configureEmbeddedHTMLSupport)(props), { values = {}, defaultRichTextElements = {} } = _a, configuredProps = __rest(_a, ["values", "defaultRichTextElements"]);
            const innerIntl = (0, react_intl_1.createIntl)(Object.assign({ locale: 'en-US', textComponent: 'span', wrapRichTextChunksInFragment: false }, configuredProps), intlCache);
            const intlFormatMessage = innerIntl.formatMessage;
            Object.assign(innerIntl, {
                formatMessage: (messageDescriptor, messageVars) => {
                    var _a, _b;
                    if (debug || debugCookie) {
                        return `${constants_1.DEBUG_WRAPPER_TEXT}${(_a = messageDescriptor.id) !== null && _a !== void 0 ? _a : 'NO_ID_FOUND'}${constants_1.DEBUG_WRAPPER_TEXT}`;
                    }
                    return intlFormatMessage(messageDescriptor, Object.assign(Object.assign(Object.assign({}, defaultRichTextElements), (messageDescriptor.id ? (_b = values[messageDescriptor.id]) !== null && _b !== void 0 ? _b : {} : {})), messageVars));
                }
            });
            return innerIntl;
            // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to recreate the intl object outside of debugging
        }, [debug]);
        (0, useNodeHighlighter_1.useNodeHighlighter)(debug || debugCookie);
        return react_1.default.createElement(react_intl_1.RawIntlProvider, { value: intl }, children);
    };
    return InternalIntlProvider;
};
exports.createIntlProvider = createIntlProvider;
/**
 * IntlProvider component that provides internationalization context to its children.
 *
 * The IntlProvider component uses the `useMemo` hook to create an `intl` object that is used to format messages.
 * It supports debugging by prefixing formatted messages with a debug prefix if the `debug` prop is set or a specific cookie is present.
 *
 * The IntlProvider also restores the HTML element support that was removed in `react-intl` v4.
 *
 * @example
 * ```tsx
 * const App = () => (
 *   <MyIntlProvider locale="en-US" messages={messages}>
 *     <MyComponent />
 *   </MyIntlProvider>
 * );
 * ```
 */
exports.IntlProvider = (0, exports.createIntlProvider)();
//# sourceMappingURL=kyruus-intl-provider.js.map